import { useState, useEffect, useMemo } from "react";
import { motion } from "motion/react";
import { Clock, Timer, User, CalendarClock } from "lucide-react";
import { useJobPostProgressContext } from "../../../../context/jobPostProgressFrom";
import { StepFooterNav } from "../../shared/NavigationFooter";

// UI Components
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../../../components/ui/tabs";

export function Step2({ nextStep, preStep, currentStep, totalSteps }) {
  const {
    jobPostProgress,
    setScheduledTime,
    setServiceDuration,
    setReferredBy,
    setWindow, // Assuming this exists based on instructions
  } = useJobPostProgressContext();

  // --- State ---

  // Time State
  const [hour, setHour] = useState(jobPostProgress?.scheduledTime?.hour || "09");
  const [minute, setMinute] = useState(jobPostProgress?.scheduledTime?.minute || "00");
  const [period, setPeriod] = useState(jobPostProgress?.scheduledTime?.period || "AM");

  // Window State
  const [selectedWindow, setSelectedWindow] = useState(
    jobPostProgress?.window || "5h"
  );

  // Other State
  const [selectedDuration, setSelectedDuration] = useState(
    jobPostProgress?.serviceDuration ?? ""
  );
  const [selectedReferredBy, setSelectedReferredBy] = useState(
    jobPostProgress?.referredBy ?? ""
  );

  const durations = [
    "Less than 30 min",
    "30-60 min",
    "60-90 min",
    "90-120 min",
    "More than 120 min",
  ];

  const windows = ["2h", "3h", "4h", "5h"];

  // --- Helpers ---

  // Calculate the arrival text (e.g., "Arrive between 10:00 AM and 1:00 PM")
  const arrivalRangeText = useMemo(() => {
    if (!hour || !selectedWindow) return "";

    const startH = parseInt(hour, 10);
    const windowH = parseInt(selectedWindow.replace("h", ""), 10);

    // Normalize start time to minutes from start of day for calculation
    let startTotalMins = (startH % 12) * 60 + parseInt(minute, 10);
    if (period === "PM") startTotalMins += 12 * 60;

    // Calculate end time
    const endTotalMins = startTotalMins + (windowH * 60);

    // Formatter
    const formatTime = (totalMinutes) => {
      let h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const p = h >= 12 && h < 24 ? "PM" : "AM";

      h = h % 12;
      if (h === 0) h = 12;

      return `${h}:${String(m).padStart(2, "0")} ${p}`;
    };

    const startStr = `${hour}:${minute} ${period}`;
    const endStr = formatTime(endTotalMins);

    return `Arrive between ${startStr} and ${endStr}`;
  }, [hour, minute, period, selectedWindow]);

  // --- Handlers ---

  const handleNext = () => {
    // Save Time
    setScheduledTime(hour, minute, period);

    // Save Window
    if (setWindow) {
      setWindow(selectedWindow);
    }

    // Save Others
    setServiceDuration(selectedDuration);
    setReferredBy(selectedReferredBy);

    nextStep();
  };

  const handleBack = () => {
    preStep();
  };

  // Sync state if context changes externally (or on re-visit)
  useEffect(() => {
    if (jobPostProgress?.scheduledTime) {
      setHour(jobPostProgress.scheduledTime.hour || "09");
      setMinute(jobPostProgress.scheduledTime.minute || "00");
      setPeriod(jobPostProgress.scheduledTime.period || "AM");
    }
    if (jobPostProgress?.window) {
      setSelectedWindow(jobPostProgress.window);
    }
    setSelectedDuration(jobPostProgress?.serviceDuration ?? "");
    setSelectedReferredBy(jobPostProgress?.referredBy ?? "");
  }, [jobPostProgress]);

  // --- Render ---
  return (
    <div className="min-h-screen py-10">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl font-bold text-black">Timing & Duration</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            When do you need the service and how long will it take?
          </p>
        </div>

        {/* Card */}
        <div className="bg-white border rounded-lg shadow-sm p-6 max-w-2xl mx-auto space-y-8">

          {/* 1. Start Time Selection */}
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-lg font-medium">
              <Clock className="w-5 h-5" />
              Service Start Time
            </label>

            <div className="flex items-center gap-2">
              {/* Hour Select */}
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger className="w-[80px] h-11 bg-zinc-50">
                  <SelectValue placeholder="Hour" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                    const hStr = String(h).padStart(2, "0");
                    return (
                      <SelectItem key={hStr} value={hStr}>
                        {hStr}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <span className="text-xl font-bold text-gray-400">:</span>

              {/* Minute Select */}
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger className="w-[80px] h-11 bg-zinc-50">
                  <SelectValue placeholder="Min" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="00">00</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                </SelectContent>
              </Select>

              {/* AM/PM Tabs */}
              <div className="ml-2">
                <Tabs value={period} onValueChange={setPeriod} className="h-11">
                  <TabsList className="h-11">
                    <TabsTrigger value="AM" className="h-9 px-4">AM</TabsTrigger>
                    <TabsTrigger value="PM" className="h-9 px-4">PM</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
          </div>

          {/* 2. Arrival Window */}
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-lg font-medium">
              <CalendarClock className="w-5 h-5" />
              Window
            </label>

            <Tabs value={selectedWindow} onValueChange={setSelectedWindow} className="w-full">
              <TabsList className="w-full h-12 grid grid-cols-4 bg-zinc-100">
                {windows.map((w) => (
                  <TabsTrigger key={w} value={w} className="h-10 text-base">
                    {w}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {/* Dynamic Calculated Text */}
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              key={arrivalRangeText}
              className="bg-blue-50 text-blue-700 px-4 py-3 rounded-md text-sm font-medium border border-blue-100 flex items-center justify-center"
            >
              {arrivalRangeText}
            </motion.div>
          </div>

          <div className="w-full h-px bg-gray-100 my-6" />

          {/* 3. Duration */}
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-lg font-medium">
              <Timer className="w-5 h-5" />
              Service Duration (Estimate)
            </label>
            <div className="space-y-2">
              {durations.map((duration) => {
                const active = selectedDuration === duration;
                return (
                  <motion.div
                    key={duration}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => setSelectedDuration(duration)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 flex items-center justify-between ${active
                      ? "border-black/50 bg-secondary text-white"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                  >
                    <span className="text-base">{duration}</span>
                    <div
                      className={`w-3.5 h-3.5 rounded-full border-2 ${active ? "bg-white border-black/50" : "border-gray-300"
                        }`}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* 4. Referred By */}
          <div className="space-y-3 mt-6 pt-4 border-t border-gray-100">
            <label className="flex items-center gap-2 text-lg font-medium">
              <User className="w-5 h-5" />
              Referred By (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter referrer's name"
              value={selectedReferredBy}
              onChange={(e) => setSelectedReferredBy(e.target.value)}
              className="w-full h-11 px-4 border rounded-lg text-base bg-white"
            />
          </div>
        </div>

        {/* Footer Navigation */}
        <StepFooterNav
          currentStep={currentStep}
          totalSteps={totalSteps}
          onBack={handleBack}
          onNext={handleNext}
          nextDisabled={!selectedDuration}
        />
      </div>
    </div>
  );
}